import BoxCheckIcon from "@redotech/redo-icons/package-check.svg";
import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { ShopifyFulfillmentHoldReason } from "@redotech/redo-model/fulfillments/shopify-fulfillment-schemas";
import { DeprecatedDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-picker";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldProps,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoRadioGroup,
  RedoRadioGroupProps,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { HoldReasonDropdown } from "../../common/place-on-hold/place-on-hold-modal";

export interface AttributesForSplitShipmentModalData {
  internalNote: string;
  newStatus: FulfillmentGroupStatus;
  onHoldReason: ShopifyFulfillmentHoldReason;
  onHoldUntil?: Date;
}

export interface AttributesForSplitShipmentModalProps {
  onSubmit: (data: AttributesForSplitShipmentModalData) => void;
  onCloseRequested: () => void;
  title?: string;
  subtitle?: string;
  primaryButtonText?: string;
}

export const AttributesForSplitShipmentModal = memo(
  function PostSplitActionModal({
    onSubmit,
    onCloseRequested,
    title = "Partially fulfill order",
    subtitle = "Any items remaining in this order that are not verified will be removed from this view and added to a new shipment.",
    primaryButtonText = "Partially fulfill order",
  }: AttributesForSplitShipmentModalProps) {
    const [data, setData] = useState<AttributesForSplitShipmentModalData>({
      internalNote: "",
      newStatus: FulfillmentGroupStatus.Open,
      onHoldReason: ShopifyFulfillmentHoldReason.Other,
    });

    const submitDisabled = !data.newStatus;

    const modalProps: RedoModalProps = {
      onModalCloseRequested: onCloseRequested,
      title,
      subtitle,
      modalSize: "sm",
      primaryButton: {
        text: primaryButtonText,
        onClick: () => onSubmit(data),
        theme: "success",
        disabled: submitDisabled,
      },
      secondaryButton: { text: "Cancel", onClick: onCloseRequested },
      TitleIcon: BoxCheckIcon,
      theme: "success",
    };
    return (
      <RedoModal {...modalProps} noPadding>
        <AttributesForSplitShipmentForm data={data} setData={setData} />
      </RedoModal>
    );
  },
);

export const AttributesForSplitShipmentForm = memo(
  function AttributesForSplitShipmentForm({
    data,
    setData,
  }: {
    data: AttributesForSplitShipmentModalData;
    setData: (data: AttributesForSplitShipmentModalData) => void;
  }) {
    const textAreaProps: RedoTextareaInputFieldProps = {
      label: "Internal note (optional)",
      value: data.internalNote,
      placeholder: "Enter internal note",
      setValue: (value) => setData({ ...data, internalNote: value }),
    };

    const radioGroupProps: RedoRadioGroupProps<FulfillmentGroupStatus> = {
      label: "Remaining items shipment status",
      options: [
        { label: "Open", id: FulfillmentGroupStatus.Open },
        { label: "On hold", id: FulfillmentGroupStatus.OnHold },
      ],
      value: data.newStatus,
      setValue: (value) =>
        setData({ ...data, newStatus: value, onHoldUntil: undefined }),
    };

    return (
      <Flex borderColor="secondary" borderTopWidth="1px" dir="column" gap="lg">
        <Flex dir="column" p="2xl">
          <RedoTextareaInputField {...textAreaProps} />
          <RedoRadioGroup {...radioGroupProps} />
          {data.newStatus === FulfillmentGroupStatus.OnHold && (
            <Flex dir="column" gap="sm">
              <HoldReasonDropdown
                reasonOption={
                  data.onHoldReason ?? ShopifyFulfillmentHoldReason.Other
                }
                setReasonOption={(reason) =>
                  setData({ ...data, onHoldReason: reason })
                }
              />
              <Flex dir="column" gap="sm">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  On hold until
                </Text>
                <DeprecatedDatePicker
                  buttonSize="sm"
                  date={data.onHoldUntil}
                  minDate={new Date()}
                  setDate={(value) => setData({ ...data, onHoldUntil: value })}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  },
);
